import { FoodListingWithDonor } from "../types";
import {
  MapPinIcon,
  ClockIcon,
  CurrencyDollarIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { cn } from "../utils/cn";

interface FoodCardProps {
  listing: FoodListingWithDonor;
  foodCategories: any[];
  users: any[];
  currentUserId?: string;
  onClaim?: (id: string) => void;
  onRequest?: (id: string) => void;
  onDelivery?: () => void;
  onEdit?: (listing: any) => void;
}

function toDate(val: any): Date | null {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (val.toDate && typeof val.toDate === "function") return val.toDate();
  if (typeof val === "string" || typeof val === "number") {
    const d = new Date(val);
    return isNaN(d.valueOf()) ? null : d;
  }
  return null;
}

// Gets the full category object, whether stored as object or id
function getCategoryObj(categoryField: any, foodCategories: any[]) {
  if (categoryField && typeof categoryField === "object" && categoryField.name)
    return categoryField;
  if (typeof categoryField === "string" && foodCategories.length > 0)
    return (
      foodCategories.find((cat: any) => cat.id === categoryField) || {
        name: "Uncategorized",
        icon: "❓",
        color: "bg-gray-200 text-gray-700",
      }
    );
  return {
    name: "Uncategorized",
    icon: "❓",
    color: "bg-gray-200 text-gray-700",
  };
}

// Gets the full donor object (with name), whether stored as object, email, or id
function getDonorObj(donorField: any, users: any[]) {
  if (donorField && donorField.name) return donorField;
  // Find in users by email or id
  if (donorField && donorField.email) {
    return (
      users.find((u: any) => u.email === donorField.email) || {
        name: donorField.email,
        stats: { donationsGiven: "?" },
      }
    );
  }
  if (donorField && donorField.id) {
    return (
      users.find((u: any) => u.id === donorField.id) || {
        name: "Unknown Donor",
        stats: { donationsGiven: "?" },
      }
    );
  }
  return { name: "Unknown Donor", stats: { donationsGiven: "?" } };
}

export default function FoodCard({
  listing,
  foodCategories,
  users,
  currentUserId,
  onClaim,
  onRequest,
  onDelivery,
  onEdit,
}: FoodCardProps) {
  const expiryDate = toDate(listing.expiryDate);
  const now = Date.now();
  const timeUntilExpiry =
    expiryDate && expiryDate.getTime
      ? Math.ceil((expiryDate.getTime() - now) / (1000 * 60 * 60))
      : null;
  const isUrgent = timeUntilExpiry !== null && timeUntilExpiry <= 6;

  const category = getCategoryObj(listing.category, foodCategories);

  // Use the joined donor data from the new hook if available
  let donorName = "Unknown Donor";
  let donorInitial = "?";
  let donationsGiven: string | number = "?";
  let donorAvatar = null;

  if (listing.donor_name) {
    // Data from the new hook with joined donor info
    donorName = listing.donor_name;
    donorInitial = listing.donor_name.charAt(0);
    donationsGiven = listing.donation_count || 0;
    donorAvatar = listing.donor_avatar;
  } else {
    // Fallback to old method
    const donor = getDonorObj(listing.donor, users);
    donorName = donor.name || "Unknown Donor";
    donorInitial = donor.name ? donor.name.charAt(0) : "?";
    donationsGiven =
      typeof donor.stats?.donationsGiven === "number"
        ? donor.stats.donationsGiven
        : "?";
  }

  // Debug logging
  console.log("FoodCard donor debug:", {
    listing_id: listing.id,
    donor_name: donorName,
    donor_avatar: donorAvatar,
    donation_count: donationsGiven,
    created_by: listing.created_by,
    has_joined_data: !!listing.donor_name,
  });

  // Check if this is the donor's own listing
  const isDonorOwnListing =
    currentUserId &&
    (listing.donorId === currentUserId ||
      (listing as any).donor_id === currentUserId);

  // Check if listing is unavailable (quantity is 0 or status is not available)
  const parseQuantity = (qty: any) => {
    if (!qty) return 0;
    if (typeof qty === "number") return qty;
    if (typeof qty === "string") {
      const match = qty.match(/\d+/);
      return match ? parseInt(match[0]) : 0;
    }
    return 0;
  };

  const parsedQuantity = parseQuantity(listing.quantity);
  const isUnavailable = listing.status !== "available" || parsedQuantity <= 0;

  // Debug log for troubleshooting
  if (isDonorOwnListing) {
    console.log(`Food Card Debug - ${listing.title}:`, {
      quantity: listing.quantity,
      parsedQuantity,
      status: listing.status,
      isUnavailable,
    });
  }

  return (
    <div
      className={cn(
        "relative transition-all duration-700 card-floating group",
        isUnavailable && "opacity-60 bg-slate-100"
      )}
    >
      {/* Image */}
      <div className="relative mb-4 overflow-hidden rounded-xl">
        <img
          src={
            listing.images?.[0] ||
            "https://via.placeholder.com/300x200?text=No+Image"
          }
          alt={listing.title}
          className={cn(
            "object-cover w-full h-48 transition-all duration-500 group-hover:scale-105 group-hover:rotate-1",
            isUnavailable && "grayscale"
          )}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

        {/* Type Badge */}
        <div className="absolute top-3 left-3">
          <span
            className={cn(
              "inline-flex items-center px-3 py-1 text-xs font-bold rounded-lg shadow-lg backdrop-blur-sm",
              listing.type === "free"
                ? "bg-emerald-500/90 text-white border border-emerald-400/50"
                : "bg-amber-500/90 text-white border border-amber-400/50"
            )}
          >
            {listing.type === "free" ? "🆓 Free" : "💰 Half Price"}
          </span>
        </div>

        {/* Status Badges */}
        {isUrgent && !isUnavailable && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center px-3 py-1 text-xs font-bold rounded-lg shadow-lg bg-red-500/90 text-white border border-red-400/50 backdrop-blur-sm animate-pulse">
              ⚡ Urgent
            </span>
          </div>
        )}
        {isUnavailable && (
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center px-3 py-1 text-xs font-bold rounded-lg shadow-lg bg-slate-200/90 text-slate-700 border border-slate-300/50 backdrop-blur-sm">
              ❌ Not Available
            </span>
          </div>
        )}

        {/* Edit Button */}
        {isDonorOwnListing && (
          <div className="absolute bottom-3 right-3">
            <button
              onClick={() => onEdit?.(listing)}
              className="p-2 text-white bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-lg shadow-lg hover:shadow-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-500 transform hover:scale-105 hover:rotate-2"
              title="Edit listing"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <div className="space-y-4">
        <div>
          <h3 className="mb-2 text-xl font-bold text-slate-900 group-hover:text-emerald-600 transition-colors duration-500">
            {listing.title}
          </h3>
          <p className="text-slate-600 line-clamp-2 leading-relaxed text-sm">
            {listing.description}
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <span
            className={cn(
              "inline-flex items-center px-3 py-1 text-xs font-bold rounded-lg shadow-md",
              category.color
            )}
          >
            {category.icon} {category.name}
          </span>
          <span className="text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1 rounded-lg shadow-sm">
            {listing.quantity}
          </span>
        </div>

        <div className="space-y-3">
          <div className="flex items-center text-sm text-slate-600">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-100 mr-3 shadow-md">
              <MapPinIcon className="w-4 h-4 text-emerald-600" />
            </div>
            <span className="font-bold text-sm">
              {listing.pickupLocation?.city || "Unknown"}
              {listing.pickupLocation?.district
                ? `, ${listing.pickupLocation.district}`
                : ""}
            </span>
          </div>
          <div className="flex items-center text-sm text-slate-600">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 mr-3 shadow-md">
              <ClockIcon className="w-4 h-4 text-amber-600" />
            </div>
            <span
              className={cn("font-bold text-sm", isUrgent && "text-red-600")}
            >
              {timeUntilExpiry !== null
                ? timeUntilExpiry > 0
                  ? `${timeUntilExpiry}h remaining`
                  : "Expired"
                : "No Expiry"}
            </span>
          </div>
        </div>

        {listing.type === "half-price" && listing.price && (
          <div className="flex items-center text-sm bg-amber-50 p-3 rounded-lg shadow-md border border-amber-200">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 mr-3 shadow-md">
              <CurrencyDollarIcon className="w-4 h-4 text-amber-600" />
            </div>
            <span className="font-bold text-amber-700 text-lg">
              LKR {listing.price}
            </span>
          </div>
        )}

        {/* Donor Info */}
        <div className="flex items-center pt-4 space-x-3 border-t border-slate-200">
          <div className="relative">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-100 to-teal-200 overflow-hidden ring-2 ring-emerald-200 shadow-md">
              {donorAvatar ? (
                <img
                  src={donorAvatar}
                  alt={donorName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-lg font-bold text-emerald-700">
                  {donorInitial}
                </span>
              )}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white shadow-md"></div>
          </div>
          <div className="flex-1">
            <p className="font-bold text-slate-900 text-base">{donorName}</p>
            <p className="text-xs font-semibold text-slate-500">
              {donationsGiven} donations • Verified
            </p>
          </div>
        </div>
        {/* Actions */}
        <div className="flex pt-4 space-x-3">
          {isDonorOwnListing ? (
            <button
              onClick={() => onEdit?.(listing)}
              className="flex-1 btn-outline"
            >
              ✏️ Edit Listing
            </button>
          ) : (
            <>
              {listing.type === "free" ? (
                <button
                  onClick={() => onClaim?.(listing.id)}
                  className={cn(
                    "flex-1 font-bold text-sm",
                    isUnavailable
                      ? "bg-slate-200 text-slate-500 cursor-not-allowed rounded-lg py-3"
                      : "btn-primary"
                  )}
                  disabled={isUnavailable}
                >
                  {isUnavailable ? "❌ Not Available" : "🎁 Claim Food"}
                </button>
              ) : (
                <button
                  onClick={() => onRequest?.(listing.id)}
                  className={cn(
                    "flex-1 font-bold text-sm",
                    isUnavailable
                      ? "bg-slate-200 text-slate-500 cursor-not-allowed rounded-lg py-3"
                      : "btn-secondary"
                  )}
                  disabled={isUnavailable}
                >
                  {isUnavailable ? "❌ Not Available" : "🛒 Request Food"}
                </button>
              )}
              {listing.deliveryRequested && !isUnavailable && (
                <button onClick={onDelivery} className="px-4 btn-outline">
                  🚚
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
